import { api, ApiError } from '../lib/api.js';
import { el, render, fmtDateOnly, fmtRelative, sortableHeader } from '../lib/dom.js';
import { canWrite } from '../lib/session.js';
import {
  pageHeader,
  kpiCard,
  button,
  avatar,
  progressBar,
  usageTone,
  searchField,
  filterSelect,
  actionMenu,
  confirmDialog,
  renderPagination,
  skeletonRows,
  emptyState,
  errorState,
  fullRow,
  toast,
  toastError,
} from '../lib/ui.js';
import { orgStatusBadge } from '../lib/status.js';

// ---------------------------------------------------------------------------
// Organisation list. Search, filters, paging, and sorting are all
// server-side — the org table is shared with a live product and could grow
// well past what a client-side filter/sort should hold.
// ---------------------------------------------------------------------------

const PAGE_SIZE = 25;
const COLS = 8;

function csvEscape(value) {
  const s = String(value ?? '');
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

export function orgsView({ navigate, meta }) {
  const state = { search: '', status: 'all', configMode: '', created: 'all', sortBy: 'createdAt', sortDir: 'desc', page: 1, total: 0 };

  const kpiSlot = el('div', { class: 'kpi-row' });
  const tbody = el('tbody', {});
  const footer = el('div', {});
  const theadRow = el('tr', {});

  const exportBtn = button('Export CSV', {
    title: 'Download the organisations matching the current search and filters as a CSV file. Read-only — nothing is changed.',
    onClick: () => void exportCsv(),
  });

  function listParams(extra = {}) {
    return {
      search: state.search,
      status: state.status,
      configMode: state.configMode || undefined,
      created: state.created,
      sortBy: state.sortBy,
      sortDir: state.sortDir,
      ...extra,
    };
  }

  const reload = () => {
    state.page = 1;
    void load();
  };

  const search = searchField({
    placeholder: 'Search organisations…',
    label: 'Search organisations by name',
    onSearch: (v) => {
      state.search = v;
      reload();
    },
  });

  async function loadStats() {
    try {
      const s = await api.orgStats();
      render(
        kpiSlot,
        kpiCard({ label: 'Total organisations', value: String(s.total), iconName: 'kpi-building', tone: 'indigo', meta: 'Live, excluding deleted' }),
        kpiCard({ label: 'On trial', value: String(s.onTrial), iconName: 'nav-trials', tone: 'amber', meta: 'Organisations on the Trial plan' }),
        kpiCard({
          label: 'Expiring ≤ 7 days',
          value: String(s.expiringSoon),
          iconName: 'shield-alert',
          tone: s.expiringSoon > 0 ? 'red' : 'green',
          meta: el('button', { type: 'button', class: 'link-btn', onclick: () => navigate('/trials') }, 'View expiring trials →'),
        }),
        kpiCard({ label: 'Advanced config', value: String(s.advancedConfig), iconName: 'menu-edit', tone: 'blue', meta: 'Organisations in Advanced mode' }),
      );
    } catch {
      // Non-fatal: the table itself still loads independently.
      render(kpiSlot);
    }
  }

  function sortHeader(label, field, extraClass = '') {
    return sortableHeader(state, field, label, reload, extraClass);
  }

  function renderHead() {
    render(
      theadRow,
      sortHeader('Organisation', 'name'),
      el('th', {}, 'Status'),
      sortHeader('Members', 'memberCount'),
      el('th', {}, 'Projects'),
      el('th', { title: 'Coco AI trial credits used, as a share of the organisation’s limit.' }, 'Coco AI usage'),
      sortHeader('Created', 'createdAt'),
      el('th', {}, 'Last activity'),
      el('th', { class: 'col-actions' }, 'Actions'),
    );
  }

  async function setStatus(org, isActive) {
    const ok = await confirmDialog({
      tone: isActive ? 'green' : 'red',
      iconName: isActive ? 'check-circle' : 'menu-deactivate',
      title: isActive ? `Activate ${org.name}?` : `Deactivate ${org.name}?`,
      text: isActive
        ? 'Members can use the app again on their next request.'
        : 'Every user in this organisation will be locked out of the app on their next request. Memberships, accounts and data are untouched, and this is reversed by "Activate".',
      confirmLabel: isActive ? 'Activate' : 'Deactivate',
      onConfirm: () => api.setOrgStatus(org.id, isActive),
    });
    if (ok) {
      toast(`${org.name} ${isActive ? 'activated' : 'deactivated'}.`);
      void load();
      void loadStats();
    }
  }

  async function restore(org) {
    let result = null;
    const ok = await confirmDialog({
      tone: 'green',
      iconName: 'check-circle',
      title: `Restore ${org.name}?`,
      text:
        'Memberships closed by the deletion are reinstated. Members removed individually beforehand stay removed, ' +
        'and join links cancelled by the deletion are not revived — resend those.',
      confirmLabel: 'Restore organisation',
      onConfirm: async () => {
        result = await api.restoreOrg(org.id);
      },
    });
    if (!ok) return;
    toast(`${org.name} restored.`);
    // An org restored with no OWNER cannot publish config or approve
    // exceptions in the tenant app, and nothing in the product surfaces that.
    if (result && result.hasOwner === false) {
      toast(`${org.name} has no active OWNER — add one from the organisation page.`, { tone: 'error', timeout: 9000 });
    }
    void load();
    void loadStats();
  }

  function row(org) {
    const deleted = org.deletedAt !== null;
    const cocoPct = org.cocoPromptLimit > 0 ? (org.cocoPromptsUsed / org.cocoPromptLimit) * 100 : 0;
    const open = (tab) => navigate(`/orgs/${org.id}${tab ? `?tab=${tab}` : ''}`);

    const menu = actionMenu(
      [
        { label: 'View organisation', iconName: 'menu-view', onSelect: () => open() },
        { label: 'Manage members', iconName: 'menu-users', onSelect: () => open('members') },
        { label: 'View projects', iconName: 'menu-projects', onSelect: () => open('projects') },
        canWrite() && !deleted && { label: 'Trial & settings', iconName: 'menu-edit', onSelect: () => open('settings') },
        canWrite() && !deleted && org.isActive && { label: 'Deactivate', iconName: 'menu-deactivate', danger: true, onSelect: () => void setStatus(org, false) },
        canWrite() && !deleted && !org.isActive && { label: 'Activate', iconName: 'check-circle', onSelect: () => void setStatus(org, true) },
        canWrite() && deleted && { label: 'Restore organisation', iconName: 'check-circle', onSelect: () => void restore(org) },
      ],
      { label: `Actions for ${org.name}` },
    );

    return el(
      'tr',
      { class: 'is-clickable', onclick: (e) => !e.target.closest('button, a') && open() },
      el(
        'td',
        {},
        el(
          'div',
          { class: 'cell-main' },
          avatar(org.name, { square: true }),
          el(
            'div',
            { class: 'cell-main__text' },
            el('span', { class: 'cell-title', title: org.name }, org.name),
            el('span', { class: 'cell-sub mono' }, org.customerId || org.id.slice(0, 8)),
          ),
        ),
      ),
      el('td', {}, el('div', { class: 'cell-badges' }, orgStatusBadge(org), org.configMode === 'ADVANCED' ? el('span', { class: 'role-chip' }, 'Advanced') : null)),
      el(
        'td',
        { class: 'nowrap' },
        `${org.memberCount} user${org.memberCount === 1 ? '' : 's'}`,
        // Deactivated at the membership or the account level — shown only
        // when non-zero, so a healthy org carries no permanent "0" badge.
        org.deactivatedMemberCount > 0
          ? el(
              'div',
              {},
              el(
                'span',
                {
                  class: 'cell-sub',
                  title:
                    `${org.deactivatedMemberCount} of ${org.memberCount} deactivated — blocked at login, membership not removed. ` +
                    'Open the organisation to see which, and whether it is for this org only or their whole account.',
                },
                `${org.deactivatedMemberCount} deactivated`,
              ),
            )
          : null,
      ),
      el('td', { class: 'nowrap' }, `${org.projectCount} `, el('span', { class: 'cell-sub' }, `/ ${org.maxProjects}`)),
      el(
        'td',
        {},
        el(
          'div',
          { class: 'cell-usage', title: `${org.cocoPromptsUsed} of ${org.cocoPromptLimit} Coco AI credits used` },
          progressBar(cocoPct, { tone: usageTone(cocoPct) }),
          el('span', { class: 'cell-usage__pct' }, `${Math.round(cocoPct)}%`),
        ),
      ),
      el('td', { class: 'nowrap' }, fmtDateOnly(org.createdAt)),
      el('td', { class: 'nowrap' }, fmtRelative(org.lastActivityAt)),
      el('td', { class: 'col-actions' }, menu),
    );
  }

  async function load() {
    renderHead();
    render(tbody, skeletonRows(COLS, 6));
    try {
      const data = await api.listOrgs(listParams({ page: state.page, pageSize: PAGE_SIZE }));
      state.total = data.total;
      if (data.organisations.length === 0) {
        const filtered = state.search !== '' || state.status !== 'all' || state.configMode !== '' || state.created !== 'all';
        render(
          tbody,
          fullRow(
            COLS,
            emptyState({
              title: filtered ? 'No organisations match' : 'No organisations yet',
              text: filtered ? 'Try a different search or clear the filters.' : 'Create your first organisation to start onboarding customers.',
              action: !filtered && canWrite() ? button('Add organisation', { variant: 'primary', iconName: 'plus', onClick: () => navigate('/orgs/new') }) : null,
            }),
          ),
        );
      } else {
        render(tbody, data.organisations.map(row));
      }
      renderPagination(footer, {
        page: state.page,
        pageSize: PAGE_SIZE,
        total: state.total,
        noun: 'organisations',
        onPage: (n) => {
          state.page = n;
          void load();
        },
      });
    } catch (err) {
      render(tbody, fullRow(COLS, errorState({ title: 'Failed to load organisations', text: err instanceof ApiError ? err.message : 'Check the API and try again.', onRetry: () => void load() })));
      render(footer);
    }
  }

  /**
   * Fetches every row matching the current search/filters (ignoring
   * pagination, respecting the current sort) and downloads it as CSV. Reuses
   * the same list endpoint the table uses — no separate export endpoint.
   */
  async function exportCsv() {
    exportBtn.disabled = true;
    exportBtn.textContent = 'Exporting…';
    try {
      const rows = [];
      // 100 is the shared pagination schema's hard max (core/schema-utils.ts)
      // — the API rejects anything higher with a 400.
      const EXPORT_PAGE_SIZE = 100;
      for (let page = 1; page <= 500; page += 1) {
        const data = await api.listOrgs(listParams({ page, pageSize: EXPORT_PAGE_SIZE }));
        rows.push(...data.organisations);
        if (rows.length >= data.total || data.organisations.length === 0) break;
      }

      const header = ['Name', 'Customer ID', 'Industry', 'Country', 'Members', 'Deactivated members', 'Projects', 'Max projects', 'Coco AI credits used', 'Coco AI credit limit', 'Config mode', 'Plan', 'Plan expired', 'Trial locked', 'Trial expired', 'Active', 'Deleted', 'Created', 'Last activity'];
      const lines = [
        header,
        ...rows.map((o) => [
          o.name,
          o.customerId ?? '',
          o.industry ?? '',
          o.country ?? '',
          o.memberCount,
          o.deactivatedMemberCount,
          o.projectCount,
          o.maxProjects,
          o.cocoPromptsUsed,
          o.cocoPromptLimit,
          o.configMode,
          o.plan,
          o.planExpired,
          o.trialLocked,
          o.trialExpired,
          o.isActive,
          o.deletedAt !== null,
          o.createdAt,
          o.lastActivityAt ?? '',
        ]),
      ].map((cols) => cols.map(csvEscape).join(','));

      const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `organisations-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      // Revoke on a later tick: some browsers start reading the download
      // asynchronously after the click handler returns.
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast(`Exported ${rows.length} organisation${rows.length === 1 ? '' : 's'}.`);
    } catch (err) {
      toastError(err instanceof ApiError ? err : null, 'Failed to export CSV.');
    } finally {
      exportBtn.disabled = false;
      exportBtn.textContent = 'Export CSV';
    }
  }

  void loadStats();
  void load();

  return el(
    'div',
    { class: 'stack' },
    pageHeader({
      title: 'Organisations',
      subtitle:
        `Manage customer organisations, access, trials and limits. Every new organisation is seeded with ` +
        `${meta?.defaultScheduleStandard?.title ?? 'its default schedule standard'} (${meta?.defaultScheduleStandard?.ruleCount ?? '—'} rules).`,
      actions: [
        exportBtn,
        canWrite()
          ? button('Add organisation', {
              variant: 'primary',
              iconName: 'plus',
              title: 'Provision a new tenant: creates the organisation, its first OWNER account and the default schedule standard. Nothing is written until you submit the form.',
              onClick: () => navigate('/orgs/new'),
            })
          : null,
      ],
    }),
    kpiSlot,
    el(
      'div',
      { class: 'toolbar' },
      search.root,
      filterSelect({
        label: 'Status',
        value: state.status,
        options: [
          { value: 'all', label: 'All statuses' },
          { value: 'trial', label: 'Trial' },
          { value: 'active', label: 'Active (trial lifted)' },
          { value: 'deleted', label: 'Deleted' },
        ],
        onChange: (v) => {
          state.status = v;
          reload();
        },
      }),
      filterSelect({
        label: 'Config',
        value: state.configMode,
        options: [
          { value: '', label: 'All modes' },
          { value: 'BASIC', label: 'Basic' },
          { value: 'ADVANCED', label: 'Advanced' },
        ],
        onChange: (v) => {
          state.configMode = v;
          reload();
        },
      }),
      filterSelect({
        label: 'Date created',
        value: state.created,
        options: [
          { value: 'all', label: 'All time' },
          { value: '7', label: 'Last 7 days' },
          { value: '30', label: 'Last 30 days' },
          { value: '90', label: 'Last 90 days' },
          { value: '365', label: 'Last 12 months' },
        ],
        onChange: (v) => {
          state.created = v;
          reload();
        },
      }),
    ),
    el('div', { class: 'table-card' }, el('div', { class: 'table-scroll' }, el('table', { class: 'table' }, el('thead', {}, theadRow), tbody)), footer),
  );
}
